
    import { defineComponent } from 'vue';
    import { getModule } from 'vuex-module-decorators';
    import SignageLayoutState, { ISignageLayoutState} from '../../store/modules/signageLayout';
    import SignageListState, { ISignageListState } from '../../store/modules/signageList';
    import OrganizationState, { IOrganizationState } from '../../store/modules/organizations';
    import TierState, { ITierState } from '../../store/modules/tier';
    import { ILayout, ISignageLayout, ISignageList } from '../../store/interfaces/ISignage';
    import Icon from '../Icon.vue';
    import { mdiMagnify, mdiDelete } from '@mdi/js';
    import { ElMessage } from 'element-plus';
    import SignageLayoutPreview from "../signage-layouts/SignageLayoutPreview.vue";
    import TierLimit from '../../components/TierLimit.vue';

    let signageLayoutState = {} as ISignageLayoutState, 
        signageListState = {} as ISignageListState,
        organizationState = {} as IOrganizationState,
        tierState = {} as ITierState;

    export default defineComponent({
        name: 'SignageLayouts',
        
        data() {
            return { 
                isSignageQuotaLoading: false as boolean,
                isSignageLayoutsLoading: false as boolean,
                isNewSignageLayoutLoading: false as boolean,
                isSignageLayoutDeleteLoading: false as boolean,
                isCreationModalVisible: false as boolean,
                isSignageListModalVisible: false,
                signageListModalTitle: '',
                selectedLayoutLists: [] as ISignageList[],
                signageLayoutsFilter: '' as string,
                selectedSignageLayouts: []as ISignageLayout[],
                currentSort: {
                    prop: 'name',
                    order: 'ascending'
                } as any,
                pageIndex: 1 as number,
                pageSize: 25 as number,
                searchDebounceTimer: undefined as ReturnType<typeof setTimeout> | undefined,
                selectedLayout: {} as ILayout,
                signageLayoutsToDelete: [] as ISignageLayout[],
                layoutsToDeleteWithLists: [] as ISignageLayout[],
                selectedSignageLayoutsSwap: [] as ISignageLayout[],
                deleteConfirmationModal: false,
                hasListManagement: false as boolean,
                signageLayoutDraft: {
                    name: ''
                },
                showLayoutTierWarning: false,

                // Icons
                // ======
                mdiMagnify,
                mdiDelete
            }
        },

        components: {
            Icon,
            SignageLayoutPreview,
            TierLimit
        },
        
        inject: [
            'windowWidth'
        ],

        watch: {
            signageLayoutsFilter() {
                if (this.searchDebounceTimer) {
                    clearTimeout(this.searchDebounceTimer);
                }
                this.searchDebounceTimer = setTimeout(() => {
                    this.pageIndex = 1;
                    this.loadSignageLayouts();
                }, 300);
            },
        },

        computed: {
            signageLayoutCreationFormRules(): any {
                return {
                    name: [
                        {
                            required: true,
                            message: 'Please enter a name.',
                            trigger: 'blur'
                        },
                    ],
                    layout: [
                        {
                            validator: this.validateSelectedLayout,
                            trigger: 'blur'
                        },
                    ]
                }
            },

            showListManagementTierCallout(): boolean {
                if (this.filteredSignageLayouts && this.filteredSignageLayouts.length > 0 && (!tierState.orgTier.sigangeSchedulingEnabled || !tierState.orgTier.sigangeListManagementEnabled)) {
                    return true;
                }
                return false;
            },

            // showLayoutTierCallout(): boolean {
            //     if (!tierState.orgTier.signageLayoutsEnabled) {
            //         return true;
            //     }
            //     return false;
            // },

            assetQuotaPercentage(): any {
                if(!organizationState.digitalSignageStorageQuota.usedQuota || !organizationState.digitalSignageStorageQuota.totalQuota) return 0;
                return (organizationState.digitalSignageStorageQuota.usedQuota/organizationState.digitalSignageStorageQuota.totalQuota)*100;
            },

            assetCapacityGb() {
                const orgTotalQuotaUsageInGb = (organizationState.digitalSignageStorageQuota.totalQuota / 1024 / 1024 / 1024);
                if (orgTotalQuotaUsageInGb === 0 ) {
                    return 'Unlimited';
                } else if (orgTotalQuotaUsageInGb > 0 && orgTotalQuotaUsageInGb < 1) {
                    return Math.floor(orgTotalQuotaUsageInGb * 1024);
                } else {
                    return orgTotalQuotaUsageInGb;
                }
            },

            memoryUnitCheck() {
                const orgTotalQuotaUsageInGb = (organizationState.digitalSignageStorageQuota.totalQuota / 1024 / 1024 / 1024);
                if (orgTotalQuotaUsageInGb > 0 && orgTotalQuotaUsageInGb < 1) {
                    return 'MB';
                } else {
                    return 'GB';
                }
            },

            usedStorageToSize() {
                const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
                let bytes = organizationState.digitalSignageStorageQuota.usedQuota;

                if (bytes === 0) return '0'
                const i = Math.floor(Math.log(bytes) / Math.log(1024));
                if (i === 0) return `${bytes} ${sizes[i]})`
                return `${(bytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`
            },

            upgradeText(): string {
                return 'Upgrade your Ditto account to unlock this feature.';
            },

            allSignageLayouts(): any {
                return signageLayoutState.allSignageLayouts;
            },

            allLayouts(): any {
                return signageLayoutState.allLayouts.sort((a: any, b: any) => {return a.order-b.order});
            },

            // The API now returns the page already filtered by name and sorted,
            // so the view just displays what the server sent.
            filteredSignageLayouts(): any {
                return this.allSignageLayouts;
            },

            signageLayoutsTotal(): number {
                return signageLayoutState.signageLayoutsTotal;
            }
        },

        methods: {
            // Unified server-side load: search term, sort, and current page are
            // all pushed to the API.
            async loadSignageLayouts() {
                this.isSignageLayoutsLoading = true;
                await signageLayoutState.fetchSignageLayouts({
                    reload: true,
                    name: this.signageLayoutsFilter || undefined,
                    sortField: this.currentSort.prop,
                    sortOrder: this.currentSort.order === 'descending' ? 'desc' : 'asc',
                    pageIndex: this.pageIndex,
                    pageSize: this.pageSize,
                });
                this.isSignageLayoutsLoading = false;
            },

            onPageChange(page: number) {
                this.pageIndex = page;
                this.loadSignageLayouts();
            },

            handleSortChange(sortInfo: any) {
                this.currentSort = {
                    prop: sortInfo.prop || 'name',
                    order: sortInfo.order || 'ascending'
                };
                this.pageIndex = 1;
                this.loadSignageLayouts();
            },

            onRowClick(row: any) {
                this.$router.push('/signage/layouts/' + row.id);
            },

            validateSelectedLayout(rule: any, value: any, callback: any) {
            if(!this.selectedLayout || !this.selectedLayout.id) {
                    callback(new Error('Please select a layout.'));
                }
                callback();
            },

            signageListsForLayout(layoutId: any) {
                return signageListState.signageListsByLayoutId(layoutId);
            },

            onSignageListClick(row: any) {
                this.isSignageListModalVisible = true;
                this.signageListModalTitle = `Signage lists assigned to ${row.name}`;
                this.selectedLayoutLists = this.signageListsForLayout(row.id);
            },

            onCancelLayoutDeletion() {
                this.layoutsToDeleteWithLists = [];
                this.signageLayoutsToDelete = [];
                this.deleteConfirmationModal = false;
            },

            async deleteSignageLayoutPrompt(signageLayout?: ISignageLayout | null) {
                this.layoutsToDeleteWithLists = [];

                if(signageLayout) {
                    this.signageLayoutsToDelete = [signageLayout];
                }
                else {
                    if(this.selectedSignageLayouts && this.selectedSignageLayouts.length > 0) {
                        this.signageLayoutsToDelete = this.selectedSignageLayouts;
                    }
                }

                for(const layout of this.signageLayoutsToDelete) {
                    if(this.signageListsForLayout(layout.id).length > 0) {
                        this.layoutsToDeleteWithLists.push(layout);
                    }
                }

                this.deleteConfirmationModal = true;
            },

            async deleteSignageLayout() {
                if(!this.signageLayoutsToDelete || this.signageLayoutsToDelete.length == 0) {
                    this.deleteConfirmationModal = false;
                    return;
                } 
                
                this.isSignageLayoutDeleteLoading = true;
                this.isSignageLayoutsLoading = true;

                for(const layout of this.signageLayoutsToDelete) {
                    const results = await signageLayoutState.deleteSignageLayout(layout as ISignageLayout);
                
                    if(!results.success) {
                        ElMessage({
                            message: `Signage layout deletion failed`,
                            type: 'error',
                        });
                        this.isSignageLayoutsLoading = false;
                        this.deleteConfirmationModal = false;
                        return;
                    }
                }
                
                this.selectedSignageLayouts = [];
                this.signageLayoutsToDelete = [];

                ElMessage({
                    message: `Successfully deleted signage layout${ this.selectedSignageLayouts.length > 1 ? 's' : '' }.`,
                    type: 'success',
                });

                this.isSignageLayoutsLoading = false;
                this.deleteConfirmationModal = false;
                this.isSignageLayoutDeleteLoading = false;
                
                await organizationState.fetchSignageAssetQuota() 
            },

            handleSelectionChange(val:any) {
                this.selectedSignageLayouts = val;
                this.selectedSignageLayoutsSwap = val;
            },

            onCancelLayoutCreation(){
                this.isCreationModalVisible = false;
                (this.$refs as any)['signageLayoutCreationForm'].clearValidate();
                this.signageLayoutDraft = {
                    name:''
                }; 
                this.selectedLayout = {} as ILayout;
            },

            async createSignageLayout() {
                // Validate the form
                let isValid = false;

                try{
                    isValid = await (this as any).$refs['signageLayoutCreationForm'].validate();
                }
                catch (error: any) {
                let errorHTML = '';
                    for(const key in error) {
                        for(const err of error[key]) {
                            errorHTML = `${errorHTML}${err.message}<br>`;
                        }
                    }

                    ElMessage({
                        dangerouslyUseHTMLString: true,
                        message: errorHTML,
                        type: 'error',
                    });
                }

                if(!isValid) return;

                let newLayout = {} as ISignageLayout;

                // Set defaults for layout
                newLayout.name = this.signageLayoutDraft.name;
                newLayout.layout = this.selectedLayout;
                newLayout.zoneBoundaryColor = '#414141';
                newLayout.globalBackgroundColor = '#00766A';
                newLayout.signageZones = [];
            
                this.isNewSignageLayoutLoading = true;

                let storeResponse = await signageLayoutState.createSignageLayout(newLayout);
                if(!storeResponse.success) {
                    ElMessage({
                        message: `Error creating signage layout`,
                        type: 'error',
                    });
                    this.isNewSignageLayoutLoading = false;
                    return;
                }

                ElMessage({
                        message: `Signage layout created`,
                        type: 'success',
                    });

                if(storeResponse && storeResponse.data) {
                    this.$router.push({ path: `/signage/layouts/${storeResponse.data.id}` });
                }

                await this.loadSignageLayouts();
                this.isCreationModalVisible = false;
                this.isNewSignageLayoutLoading = false;
                this.signageLayoutDraft = {
                    name:''
                }; 
                this.selectedLayout = {} as ILayout;
            },

            onSelectLayout(layout: ILayout) {
                if(this.selectedLayout.id == layout.id) {
                    this.selectedLayout = {} as ILayout;
                }
                else {
                    this.selectedLayout = layout;
                }
            },

            getZoneContent(layout: any) {
                for(const zone of layout.signageZones) {
                    const zoneInfo = layout.layout.zones.find((x: any) => x.zoneNumber == zone.zoneNumber);
                    zone.allowScrolling = zoneInfo.allowScrolling;
                }
                return layout;
            } 
        },

        async created() {
            signageLayoutState = getModule(SignageLayoutState);
            signageListState = getModule(SignageListState);
            organizationState = getModule(OrganizationState);
            tierState = getModule(TierState); 

            this. isSignageLayoutsLoading = true;

            await Promise.all([
                this.loadSignageLayouts(),
                signageListState.fetchSignageLists({ reload: true, includeRoomDetails: true }),
                organizationState.fetchSignageAssetQuota(),
                tierState.fetchTier(),
                signageLayoutState.fetchLayouts()
            ]);

            this.hasListManagement = tierState.orgTier.sigangeListManagementEnabled;
            this.isSignageLayoutsLoading = false;
            this.showLayoutTierWarning = !tierState.orgTier.signageLayoutsEnabled;
        }
    });
